import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";
import useVisitorTracking from "../hooks/useVisitorTracking";
import GlobalAudioPlayer from "./GlobalAudioPlayer";
import { 
  User, 
  LogOut, 
  Menu, 
  BookOpen, 
  Library, 
  CreditCard,
  Scale,
  Sparkles,
  Heart,
  Users,
  ListMusic,
  Compass,
  Package
} from "lucide-react";

export default function Layout() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [siteConfig, setSiteConfig] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Track page views
  useVisitorTracking();

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const configRes = await axios.get(`${API}/site-config`);
      setSiteConfig(configRes.data);
      
      if (user) {
        const statusRes = await axios.get(`${API}/subscription/status`, {
          
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setSubscriptionStatus(statusRes.data);
      }
    } catch (e) {
      console.error("Failed to fetch data", e);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const hasSubscription = subscriptionStatus?.has_subscription;
  const siteName = siteConfig?.site_name || "Bar Songz";

  const navLinks = [
    { href: "/browse", label: "Preview", icon: BookOpen },
    { href: "/packs", label: "Buy Songs", icon: Package },
    { href: "/library", label: "Library", icon: Library },
    { href: "/playlist", label: "Playlist", icon: ListMusic },
    { href: "/discover", label: "Discover", icon: Compass },
    { href: "/community", label: "Community", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <Scale className="h-6 w-6 text-gold" />
              <span className="font-serif text-xl font-bold text-foreground">
                {siteName}
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors hover:text-gold ${
                    location.pathname.startsWith(link.href) ? "text-gold" : "text-muted-foreground"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              {/* Subscribe CTA for non-subscribers */}
              {!hasSubscription && (
                <Button 
                  onClick={() => navigate("/subscribe")} 
                  className="hidden md:flex bg-gold text-primary-foreground hover:bg-gold/90"
                  data-testid="nav-subscribe-btn"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Subscribe Now
                </Button>
              )}

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="user-menu-trigger">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {hasSubscription && (
                        <p className="text-xs text-gold mt-1">
                          {subscriptionStatus?.tier === "premium" ? "Premium" : "Basic"} • {subscriptionStatus?.subscription?.status === "trialing" ? "Trial Active" : "Subscribed"}
                        </p>
                      )}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/library")} data-testid="menu-library">
                      <Library className="mr-2 h-4 w-4" />
                      My Library
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites">
                      <Heart className="mr-2 h-4 w-4" />
                      Favorites
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Subscription
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <div className="hidden md:flex items-center gap-2">
                  <Button variant="ghost" onClick={() => navigate("/login")} data-testid="login-btn">
                    Sign In
                  </Button>
                </div>
              )}

              {/* Mobile Menu */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <div className="flex flex-col gap-4 mt-8">
                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 text-lg font-medium py-2"
                      >
                        <link.icon className="h-5 w-5 text-gold" />
                        {link.label}
                      </Link>
                    ))}
                    
                    {!hasSubscription && (
                      <Button 
                        onClick={() => { navigate("/subscribe"); setMobileMenuOpen(false); }} 
                        className="bg-gold text-primary-foreground"
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        Subscribe Now
                      </Button>
                    )}
                    
                    {!user && (
                      <>
                        <hr className="border-border" />
                        <Button variant="outline" onClick={() => { navigate("/login"); setMobileMenuOpen(false); }}>
                          Sign In
                        </Button>
                        <Button onClick={() => { navigate("/register"); setMobileMenuOpen(false); }} className="bg-gold text-primary-foreground">
                          Create Account
                        </Button>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Sticky audio player — persists across route changes */}
      <GlobalAudioPlayer />

      {/* Footer */}
      <footer className="border-t border-border bg-card py-8 mt-auto">
        <div className="container mx-auto px-4 lg:px-8 space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-gold" />
              <span className="font-serif font-bold">{siteName}</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/privacy" className="text-muted-foreground hover:text-gold transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-muted-foreground hover:text-gold transition-colors">
                Terms of Service
              </Link>
              <a
                href="https://www.tiktok.com/@itsfreedomfancie"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Find Nye, the founder of Bar Songz, on TikTok (@itsfreedomfancie)"
                className="text-muted-foreground hover:text-gold transition-colors inline-flex items-center gap-1.5"
                data-testid="footer-tiktok-link"
                title="Find Nye on TikTok — @itsfreedomfancie"
              >
                {/* Inline TikTok glyph (lucide-react does not ship one) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V7.66a8.21 8.21 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.09Z" />
                </svg>
                <span className="hidden sm:inline">Find Nye on TikTok</span>
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              {siteConfig?.footer_text || "© 2025 Bar Songz. Law school audio for your ears and eyes."}
            </p>
          </div>

          {/* Legal Disclaimer */}
          <div
            className="text-xs text-muted-foreground leading-relaxed border-t border-border pt-5 max-w-5xl mx-auto"
            data-testid="footer-legal-disclaimer"
          >
            <p className="mb-3">
              <sup className="text-foreground">*</sup>{" "}
              <strong className="text-foreground">Nye is not an attorney.</strong>{" "}
              Nothing on this site is legal advice or the practice of law, and no attorney-client
              relationship is formed by your use of the Service. Always consult a licensed attorney
              in your jurisdiction for legal matters.
            </p>
            <p className="mb-2">
              <strong className="text-foreground">Disclaimer:</strong>{" "}
              Bar Songz is an audio study aid intended to{" "}
              <span className="text-primary font-semibold">supplement</span>—not replace—traditional
              legal education materials. Use of this site does <strong>not</strong> guarantee any
              particular outcome in law school, on any bar examination (MPRE, MBE, MEE, MPT, UBE, or
              any state-specific licensing exam), or in the practice of law. Results vary.
            </p>
            <p>
              Your use of this site is governed by our{" "}
              <Link to="/terms" className="text-foreground underline hover:text-gold transition-colors">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="text-foreground underline hover:text-gold transition-colors">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
